// Park saves the work; unpark compares (2026-10-27,
// docs/decisions/2026-10-27-park-saves-work.md, docs/contact-view.md
// "Parking").
//
// When a parked contact is opened again it regenerates, as before, and the
// new suggestion is compared with the parked one and with the CSR's saved
// work in three areas:
//   - what happened since the park: a new customer message, and the facts
//     snapshot stored with each suggestion (server/src/ai/facts-snapshot.js):
//     order status, documents (refund, RMA, RTN, replacement, cancellation),
//     the return stage, tracking, holds, the eBay case / inquiry / return;
//   - the steps: added, removed, or the same step with different defaults
//     (reason code, items, disposition, amounts, address, packages). A step
//     processed before the park is never offered again;
//   - the reply: the new draft against the reply the CSR had, by the
//     sentence comparison of step-blocks.js (whitespace, the signature and
//     the date stamp alone are not a difference; an amount is).
// Anything in those is "substantial", and the panel asks; otherwise the
// saved work stays and "nothing new" is shown.
//
// Shared by the UI (ParkComparePanel.jsx, SuggestionPanel.jsx) and the
// server's tests. Pure: no calls.

import { templateSentenceShare } from './step-blocks.js'
import { RETURN_STAGE_OPTIONS, RETURN_STAGE_IN_TRANSIT, RETURN_STAGE_DELIVERED } from './return-stage.js'

// The panel's labels, used exactly as written everywhere in it.
export const PARK_KEEP_LABEL = 'Keep my saved work'
export const PARK_USE_NEW_LABEL = 'Use new suggestion'
export const PARK_APPLY_CHOICES_LABEL = 'Apply my choices'
export const PARK_NOTHING_NEW_TEXT = 'Checked for changes: nothing new since you parked this.'
export const PARK_KEEP = 'kept'
export const PARK_NEW = 'new'

// --- Formatting --------------------------------------------------------------

// 10/27/2026 from an ISO date or timestamp (its own date, never shifted).
export function parkMdy(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''))
  return m ? `${m[2]}/${m[3]}/${m[1]}` : (value ? String(value) : '')
}

export function parkMoney(n) {
  return n == null || Number.isNaN(Number(n)) ? null : `$${Number(n).toFixed(2)}`
}

// "10/27/2026 10:42 AM ET": the restored line's time.
export function etDateTime(value) {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d).map((p) => [p.type, p.value]))
  return `${parts.month}/${parts.day}/${parts.year} ${parts.hour}:${parts.minute} ${parts.dayPeriod} ET`
}

// Whether saved work is the viewer's own (2026-10-30: unknown counts as theirs).
export function isOwnWork(work, viewer = null) {
  return !work?.savedBy || !viewer || work.savedBy === viewer
}

// "Restored your work from 10/27/2026 10:42 AM ET", or "Restored Bo's work
// from …" when someone else saved it (2026-10-30, the work-drafts row's
// display name).
export function restoredWorkLine(work, viewer = null) {
  if (!work?.savedAt) return null
  const at = etDateTime(work.savedAt)
  if (isOwnWork(work, viewer)) return `Restored your work from ${at}`
  return `Restored ${work.savedByName ?? work.savedBy}'s work from ${at}`
}

// The restored line's button when someone else's work was restored.
export const START_FROM_AI_DRAFT_LABEL = 'Start from the AI draft instead'

export function sinceParkedTitle(parkedAt) {
  return `Since you parked this (${etDateTime(parkedAt)})`
}

// --- What happened since the park -------------------------------------------

const DOC_NAMES = { refund: 'Refund', refund_denied: 'Refund denied', rma: 'RMA', replacement: 'Replacement', cancellation: 'Cancellation' }
const RMA_STATUS = {
  return_requested_awaiting_parts: 'label issued, awaiting the parts',
  refund_issued_awaiting_parts: 'refund issued, awaiting the parts',
  parts_received_refund_issued: 'parts received, refund issued',
}
// "in transit → delivered to our warehouse 10/27/2026" (Jenn's wording).
const STAGE_WORDS = { [RETURN_STAGE_IN_TRANSIT]: 'in transit', [RETURN_STAGE_DELIVERED]: 'delivered to our warehouse' }
const stageLabel = (v) => STAGE_WORDS[v] ?? RETURN_STAGE_OPTIONS.find((o) => o.value === v)?.label ?? String(v ?? 'not chosen')
const trackingLabel = (t) => `${t.carrier ? `${t.carrier} ` : ''}${t.number}`

// Documents keyed by kind and number (else by their place among that kind).
function keyedDocs(docs) {
  const out = new Map()
  const seen = {}
  for (const d of docs ?? []) {
    const n = seen[d.kind] = (seen[d.kind] ?? 0) + 1
    out.set(`${d.kind}:${d.number ?? `#${n}`}`, d)
  }
  return out
}

function docCreatedLine(d) {
  const on = d.number ? ` on ${d.number}` : ''
  const date = d.date ? `, ${parkMdy(d.date)}` : ''
  switch (d.kind) {
    case 'refund': return `Refund issued: ${parkMoney(d.amount) ?? 'amount unknown'}${on}${date}`
    case 'refund_denied': return `Refund denied ($0 return document)${on}${date}`
    case 'rma': return `RMA created${d.number ? `: ${d.number}` : ''}${date}${d.status ? ` (${RMA_STATUS[d.status] ?? d.status})` : ''}`
    case 'replacement': return `Replacement created${d.number ? `: ${d.number}` : ''}${date}${d.status ? ` (${d.status})` : ''}`
    case 'cancellation': return `Order cancelled${d.date ? `: ${parkMdy(d.date)}` : ''}`
    default: return `${DOC_NAMES[d.kind] ?? d.kind} created${on}${date}`
  }
}

function docChangedLines(was, now) {
  const name = `${DOC_NAMES[now.kind] ?? now.kind}${now.number ? ` ${now.number}` : ''}`
  const lines = []
  if (was.amount !== now.amount && (was.amount != null || now.amount != null)) lines.push({ kind: 'document', field: 'amount', docKind: now.kind, was: was.amount, now: now.amount, text: `${name} amount changed: ${parkMoney(was.amount) ?? 'none'} → ${parkMoney(now.amount) ?? 'none'}` })
  if ((was.status ?? null) !== (now.status ?? null)) {
    const label = (s) => (now.kind === 'rma' ? RMA_STATUS[s] ?? s : s) ?? 'none'
    lines.push({ kind: 'document', field: 'status', docKind: now.kind, text: `${name}: ${label(was.status)} → ${label(now.status)}` })
  }
  if ((was.date ?? null) !== (now.date ?? null) && was.date && now.date && parkMdy(was.date) !== parkMdy(now.date)) lines.push({ kind: 'document', field: 'date', docKind: now.kind, text: `${name} date: ${parkMdy(was.date)} → ${parkMdy(now.date)}` })
  return lines
}

function caseLabel(c) {
  const kind = c.kind === 'inquiry' ? 'eBay inquiry' : 'eBay case'
  return `${kind} ${c.id}`
}
const waitingOnUs = (status) => /SELLER|WAITING_FOR_SELLER|ACTION_REQUIRED/i.test(String(status ?? ''))
const statusWithWait = (s) => `${s ?? 'unknown'}${waitingOnUs(s) ? ' (waiting on us)' : ''}`

// One plain line per change, old → new, with amounts, dates and document
// numbers: [{ kind, text, ... }]. Either snapshot missing → [] (nothing can
// be said; never a guess).
export function diffFactsSnapshots(was, now) {
  if (!was || !now) return []
  const out = []
  // Order status, with the new tracking when it shipped.
  const ws = was.order?.status ?? null
  const ns = now.order?.status ?? null
  const wasTracking = new Set((was.tracking ?? []).map((t) => t.number))
  const newTracking = (now.tracking ?? []).filter((t) => t.number && !wasTracking.has(t.number))
  if (ws !== ns && (ws || ns)) {
    out.push({ kind: 'order_status', was: ws, now: ns, text: `Order status: ${ws ?? 'unknown'} → ${ns ?? 'unknown'}${newTracking.length ? `, ${newTracking.map(trackingLabel).join(', ')}` : ''}` })
  } else {
    for (const t of newTracking) out.push({ kind: 'tracking', text: `New tracking: ${trackingLabel(t)}` })
  }
  // Documents.
  const wd = keyedDocs(was.documents)
  const nd = keyedDocs(now.documents)
  for (const [k, d] of nd) {
    const old = wd.get(k)
    if (!old) out.push({ kind: 'document', field: 'created', docKind: d.kind, now: d.amount ?? null, text: docCreatedLine(d) })
    else out.push(...docChangedLines(old, d))
  }
  for (const [k, d] of wd) {
    if (!nd.has(k)) out.push({ kind: 'document', field: 'removed', docKind: d.kind, text: `Document cancelled: ${DOC_NAMES[d.kind] ?? d.kind}${d.number ? ` ${d.number}` : ''}${d.amount != null ? ` (${parkMoney(d.amount)})` : ''}` })
  }
  // The return stage.
  const wr = was.returnStage
  const nr = now.returnStage
  if ((wr?.value ?? null) !== (nr?.value ?? null) || (nr?.date && wr?.date !== nr.date)) {
    if (wr || nr) out.push({ kind: 'return_stage', text: `Return stage: ${wr ? stageLabel(wr.value) : 'not known'} → ${nr ? `${stageLabel(nr.value)}${nr.date ? ` ${parkMdy(nr.date)}` : ''}` : 'not known'}` })
  }
  // Tracking: the latest scan, or delivered, per package already known.
  const wt = new Map((was.tracking ?? []).map((t) => [t.number, t]))
  for (const t of now.tracking ?? []) {
    const old = wt.get(t.number)
    if (!old) continue
    if (t.delivered === true && old.delivered !== true) {
      out.push({ kind: 'tracking', text: `Tracking ${trackingLabel(t)}: delivered${t.latest?.date ? ` ${parkMdy(t.latest.date)}` : ''}` })
    } else if (t.latest?.date && (t.latest.date !== old.latest?.date || (t.latest.event ?? null) !== (old.latest?.event ?? null))) {
      out.push({ kind: 'tracking', text: `Tracking ${trackingLabel(t)}: latest scan ${parkMdy(t.latest.date)}${t.latest.event ? `, ${t.latest.event}` : ''}${old.latest?.date ? ` (was ${parkMdy(old.latest.date)}${old.latest.event ? `, ${old.latest.event}` : ''})` : ''}` })
    }
  }
  // Holds.
  if (was.holds != null && now.holds != null && was.holds !== now.holds) {
    out.push({ kind: 'holds', text: now.holds ? 'Order hold added: the order is on hold now' : 'Order hold removed: no holds on the order now' })
  }
  // The eBay case / inquiry and return.
  const wc = was.ebay?.caseOrInquiry ?? null
  const nc = now.ebay?.caseOrInquiry ?? null
  if (nc && (!wc || wc.id !== nc.id)) out.push({ kind: 'ebay', text: `${caseLabel(nc)} opened: ${statusWithWait(nc.status)}` })
  else if (nc && wc && (wc.status ?? null) !== (nc.status ?? null)) out.push({ kind: 'ebay', text: `${caseLabel(nc)}: ${wc.status ?? 'unknown'} → ${statusWithWait(nc.status)}` })
  if (wc && !nc) out.push({ kind: 'ebay', text: `${caseLabel(wc)} closed (no open case or inquiry now)` })
  const wret = was.ebay?.return ?? null
  const nret = now.ebay?.return ?? null
  if (nret && (!wret || wret.id !== nret.id)) out.push({ kind: 'ebay', text: `eBay return ${nret.id} opened${nret.status ? `: ${statusWithWait(nret.status)}` : ''}` })
  else if (nret && wret && (wret.status ?? null) !== (nret.status ?? null)) out.push({ kind: 'ebay', text: `eBay return ${nret.id}: ${wret.status ?? 'unknown'} → ${statusWithWait(nret.status)}` })
  if (wret && !nret) out.push({ kind: 'ebay', text: `eBay return ${wret.id} closed (no open return now)` })
  return out
}

// Customer messages received after the park, oldest first: { at, text }.
export function newCustomerMessages(messages, since) {
  const t0 = new Date(since).getTime()
  if (Number.isNaN(t0)) return []
  return (messages ?? [])
    .filter((m) => m?.direction === 'inbound')
    .map((m) => ({ id: m.id ?? null, at: m.receivedAt ?? m.sentAt ?? m.createdAt ?? null, text: plainText(m.body ?? m.text ?? '').replace(/\s+/g, ' ').trim().slice(0, 1000) }))
    .filter((m) => m.at && new Date(m.at).getTime() > t0)
    .sort((a, b) => new Date(a.at) - new Date(b.at))
}

// --- The steps -----------------------------------------------------------------

// A step's identity across two generations: its type, its kind and target.
// The nth such step in one list matches the nth in the other.
export function stepKey(a) {
  return `${a?.type ?? ''}|${a?.params?.kind ?? ''}|${a?.targetSystem ?? ''}`
}

// { oldToNew: { [oldIndex]: newIndex }, newToOld: { [newIndex]: oldIndex } }.
export function mapStepIndexes(oldActions = [], newActions = []) {
  const queue = new Map()
  ;(newActions ?? []).forEach((a, i) => {
    const k = stepKey(a)
    if (!queue.has(k)) queue.set(k, [])
    queue.get(k).push(i)
  })
  const oldToNew = {}
  const newToOld = {}
  ;(oldActions ?? []).forEach((a, i) => {
    const q = queue.get(stepKey(a))
    if (q?.length) {
      const j = q.shift()
      oldToNew[i] = j
      newToOld[j] = i
    }
  })
  return { oldToNew, newToOld }
}

// { [savedIndex]: targetIndex }: where each step of the saved work (its
// payload.steps, { type, kind, targetSystem }) is in `target`'s steps. The
// identity when the work was done on that same suggestion, else matched by
// stepKey(). Used to restore inputs and to place processed steps.
export function savedStepIndexMap(work, target) {
  const saved = work?.payload?.steps ?? []
  const targetActions = target?.proposedActions ?? []
  if (work?.suggestionId && target?.id && work.suggestionId === target.id) {
    return Object.fromEntries(saved.map((_, i) => [i, i]).filter(([i]) => i < targetActions.length))
  }
  const asActions = saved.map((x) => ({ type: x?.type, targetSystem: x?.targetSystem ?? null, params: x?.kind ? { kind: x.kind } : {} }))
  return mapStepIndexes(asActions, targetActions).oldToNew
}

// The defaults that matter when the same step comes back: the reason code,
// the items, the disposition, amounts, the address and the packages. Read
// generically from the step (any param named so), so a step with new inputs
// is covered without a change here. Free text (notes, messages) is not a
// default: a regenerated note always reads a little differently.
const DEFAULT_PARAM_RE = /reason|disposition|amount|refund|total|deduct|address|package|quantit|qty|items?$|sku|payer|pays/i
const TEXT_PARAM_RE = /^(text|message|note|body|label|links?|dependsOn.*|missingReason|options|preselected)$/i
export function stepDefaults(a) {
  if (!a) return {}
  const out = {}
  if (a.inputDefault != null) out.reasonCode = String(a.inputDefault)
  if (a.itemSelection?.defaultSelected) out.items = a.itemSelection.defaultSelected
  if (a.shippingSelection?.address) out.address = a.shippingSelection.address
  if (a.shippingSelection?.defaultMethod) out.shippingMethod = a.shippingSelection.defaultMethod
  for (const [k, v] of Object.entries(a.params ?? {})) {
    if (TEXT_PARAM_RE.test(k) || !DEFAULT_PARAM_RE.test(k) || v == null) continue
    out[k] = v
  }
  return out
}

const FIELD_NAMES = { reasonCode: 'Reason code', items: 'Items', address: 'Address', shippingMethod: 'Shipping method' }
function fieldName(k) {
  if (FIELD_NAMES[k]) return FIELD_NAMES[k]
  const words = k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}
function showValue(k, v) {
  if (v == null) return 'none'
  if (typeof v === 'number' && /amount|refund|total|deduct/i.test(k)) return parkMoney(v)
  if (Array.isArray(v)) {
    return v.map((x) => (x && typeof x === 'object' ? [x.quantity != null ? `${x.quantity} x` : null, x.sku ?? x.description ?? x.id ?? null].filter(Boolean).join(' ') : String(x))).join(', ') || 'none'
  }
  if (typeof v === 'object') return Object.values(v).filter((x) => x != null && x !== '' && typeof x !== 'object').join(', ') || 'none'
  return String(v)
}
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

// [{ field, name, was, now, text }] for one step's changed defaults.
export function diffStepDefaults(oldStep, newStep) {
  const a = stepDefaults(oldStep)
  const b = stepDefaults(newStep)
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])]
  return keys.filter((k) => !same(a[k], b[k])).map((k) => ({
    field: k, name: fieldName(k), was: a[k] ?? null, now: b[k] ?? null,
    text: `${fieldName(k)}: ${showValue(k, a[k])} → ${showValue(k, b[k])}`,
  }))
}

const stepName = (a) => a?.label || a?.type || 'Step'

// The steps compared. processedOld: the parked suggestion's step indexes
// already processed. Returns:
//   added:     [{ index, step, name }]          new steps the parked one lacked
//   removed:   [{ index, step, name }]          parked steps the new one lacks
//   changed:   [{ oldIndex, index, step, name, changes }]
//   processed: [{ oldIndex, index, name }]       processed before the park; the
//              matching new step (index, or null) is never offered again
//   map:       mapStepIndexes()'s result
export function diffSteps(oldActions = [], newActions = [], { processedOld = [] } = {}) {
  const map = mapStepIndexes(oldActions, newActions)
  const done = new Set((processedOld ?? []).map(Number))
  const added = []
  const removed = []
  const changed = []
  const processed = []
  ;(newActions ?? []).forEach((step, index) => {
    if (map.newToOld[index] == null) added.push({ index, step, name: stepName(step) })
  })
  ;(oldActions ?? []).forEach((step, oldIndex) => {
    const index = map.oldToNew[oldIndex]
    if (done.has(oldIndex)) { processed.push({ oldIndex, index: index ?? null, name: stepName(step) }); return }
    if (index == null) { removed.push({ index: oldIndex, step, name: stepName(step) }); return }
    const changes = diffStepDefaults(step, newActions[index])
    if (changes.length) changed.push({ oldIndex, index, step: newActions[index], name: stepName(newActions[index]), changes })
  })
  return { added, removed, changed, processed, map }
}

// --- The reply -----------------------------------------------------------------

const DATE_STAMP_RE = /\b(?:\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}-\d{2}-\d{2}|(?:mon|tue|wed|thu|fri|sat|sun)[a-z]*,?\s+[a-z]+\s+\d{1,2},?\s+\d{4})\b(?:\s+\d{1,2}:\d{2}(?::\d{2})?\s*(?:[ap]\.?m\.?)?(?:\s*[A-Z]{2,4})?)?/gi
function plainText(text) {
  return String(text ?? '').replace(/<\s*br\s*\/?>/gi, '\n').replace(/<\/(?:p|div|li|h\d)>/gi, '\n').replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&#39;|&apos;/gi, "'").replace(/&quot;/gi, '"').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>')
}
function withoutSignature(text, signature) {
  const t = plainText(text)
  const sig = plainText(signature).trim()
  if (!sig) return t
  const at = t.lastIndexOf(sig)
  return at >= 0 ? t.slice(0, at) + t.slice(at + sig.length) : t
}
const amountsOf = (t) => [...String(t).matchAll(/\$\s?\d[\d,]*(?:\.\d{2})?/g)].map((m) => m[0].replace(/[\s,]/g, '')).sort()
const docNumbersOf = (t) => [...String(t).matchAll(/\b[A-Z]{2,5}\d{6,}\b|\b1Z[0-9A-Z]{8,}\b/g)].map((m) => m[0]).sort()

// Whether the new draft says something different from the CSR's reply: not
// only whitespace, the signature or a date stamp. Sentence by sentence (both
// ways, every sentence found), plus the amounts and document numbers, which
// the sentence comparison treats as any number.
export function replyDiffers(saved, fresh, { signature = null } = {}) {
  const a = withoutSignature(saved, signature).replace(DATE_STAMP_RE, ' ').trim()
  const b = withoutSignature(fresh, signature).replace(DATE_STAMP_RE, ' ').trim()
  if (!a && !b) return false
  if (!a || !b) return true
  if (!same(amountsOf(a), amountsOf(b)) || !same(docNumbersOf(a), docNumbersOf(b))) return true
  return templateSentenceShare(a, b) < 1 || templateSentenceShare(b, a) < 1
}

// A word-level difference for the side-by-side view: [{ type: 'same' |
// 'removed' | 'added', text }]. Longest common subsequence over words;
// texts beyond maxWords compare as one changed block.
export function wordDiff(saved, fresh, { maxWords = 1500 } = {}) {
  const a = plainText(saved).split(/(\s+)/).filter((w) => w !== '')
  const b = plainText(fresh).split(/(\s+)/).filter((w) => w !== '')
  if (a.length > maxWords || b.length > maxWords) {
    return [{ type: 'removed', text: a.join('') }, { type: 'added', text: b.join('') }].filter((p) => p.text)
  }
  const n = a.length
  const m = b.length
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
  }
  const out = []
  const push = (type, text) => {
    const last = out[out.length - 1]
    if (last && last.type === type) last.text += text
    else out.push({ type, text })
  }
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) { push('same', a[i]); i += 1; j += 1 }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { push('removed', a[i]); i += 1 }
    else { push('added', b[j]); j += 1 }
  }
  while (i < n) { push('removed', a[i]); i += 1 }
  while (j < m) { push('added', b[j]); j += 1 }
  return out
}

// --- The whole compare -------------------------------------------------------

// parked: the suggestion the work was parked on; fresh: the regenerated one;
// work: the saved work ({ payload, parkedAt, savedAt }); messages: the
// conversation's messages; signature: the reply box's signature seed.
// Returns { substantial, facts, messages, steps, reply }.
export function compareParkedWork({ parked, fresh, work, messages = [], signature = null } = {}) {
  const since = work?.parkedAt ?? work?.savedAt ?? null
  const payload = work?.payload ?? {}
  const sameSuggestion = Boolean(parked && fresh && parked.id === fresh.id)
  const newMessages = since ? newCustomerMessages(messages, since) : []
  const facts = sameSuggestion ? [] : diffFactsSnapshots(parked?.modelMeta?.factsSnapshot ?? null, fresh?.modelMeta?.factsSnapshot ?? null)
  // The processed steps, placed in the parked suggestion's own list.
  const toParked = parked ? savedStepIndexMap(work, parked) : {}
  const processedOld = (payload.processedSteps ?? []).map((p) => toParked[p.index]).filter((i) => i != null)
  const steps = sameSuggestion
    ? { added: [], removed: [], changed: [], processed: [], map: mapStepIndexes(parked?.proposedActions, fresh?.proposedActions) }
    : diffSteps(parked?.proposedActions ?? [], fresh?.proposedActions ?? [], { processedOld })
  const savedReply = typeof payload.reply?.body === 'string' ? payload.reply.body : null
  const freshDraft = fresh?.draftReply ?? ''
  const replyChanged = !sameSuggestion && savedReply != null && replyDiffers(savedReply, freshDraft, { signature })
  const stepsChanged = steps.added.length + steps.removed.length + steps.changed.length > 0
  return {
    substantial: newMessages.length > 0 || facts.length > 0 || stepsChanged || replyChanged,
    since,
    facts,
    messages: newMessages,
    steps,
    stepsChanged,
    reply: { changed: replyChanged, saved: savedReply ?? '', fresh: freshDraft },
  }
}

// The warning on a step whose inputs the CSR changed while the facts under
// it changed: "The refund amount changed since you parked this: was $128.43,
// now $102.10." facts: diffFactsSnapshots()'s lines. Only amount changes of
// a document the step is about (a refund or return step and a refund / RMA
// document; a replacement step and a replacement) are tied to a step.
export function stepFactWarning(step, facts = []) {
  const type = String(step?.type ?? '')
  const kinds = /refund|return|rma|rtn/i.test(type) ? ['refund', 'rma'] : /replacement/i.test(type) ? ['replacement'] : []
  if (!kinds.length) return null
  const hit = (facts ?? []).find((f) => f.kind === 'document' && kinds.includes(f.docKind) && (f.field === 'amount' || f.field === 'created'))
  if (!hit) return null
  if (hit.field === 'amount') {
    const what = hit.docKind === 'refund' ? 'refund amount' : `${DOC_NAMES[hit.docKind] ?? hit.docKind} amount`
    return `The ${what} changed since you parked this: was ${parkMoney(hit.was) ?? 'none'}, now ${parkMoney(hit.now) ?? 'none'}.`
  }
  return `${hit.text.replace(/\.$/, '')}, since you parked this. Check this step before processing it.`
}

// The decision's record of the choice: { choice: 'kept' | 'new' | 'mixed',
// items: [{ area, item, choice }] }. choices: { [itemId]: 'kept' | 'new' },
// itemIds like 'reply', 'steps:added:3'.
export function parkResolutionFrom(choices = {}, { at = new Date().toISOString(), by = null } = {}) {
  const items = Object.entries(choices).map(([item, choice]) => ({ area: item.split(':')[0], item, choice }))
  const kinds = new Set(items.map((i) => i.choice))
  const choice = kinds.size === 1 ? [...kinds][0] : kinds.size === 0 ? PARK_KEEP : 'mixed'
  return { choice, items, at, by }
}
